import "bootstrap/dist/css/bootstrap.min.css";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import "./App.css";
import SwaggerContext from "./SwaggerContext";
import { logout } from "./actions/auth";
import {
  AnswerApi,
  ApiClient,
  CategoryApi,
  ErrorLogApi,
  GameSessionApi,
  PageApi,
  QuestionApi,
  RoleApi,
  UserApi,
  UserInvitationApi,
} from "./generated/src";
import { createApiCallback } from "./utils/error-handler";
import { isTokenExpiredLocally, isRefreshTokenExpiredLocally, persistTokenData } from "./utils/token-utils";
import { updateAuthUser } from "./actions/auth";
import Category from "./pages/category";
import Dashboard from "./pages/dashboard";
import Login from "./pages/login";
import Page from "./pages/page";
import Profile from "./pages/profile";
import Register from "./pages/register";
import User from "./pages/user";
import GameSelector from "./pages/game/mode-selector";
import Statistics from "./pages/statistics";
import PrivateRoute from "./routes/private-route";
import PublicRoutes from "./routes/public-routes";
import TherapistRoute from "./routes/therapist-route";
import Game from "./pages/game/game";
import Restore from "./pages/password_restore";
import CookiesPolicy from "./pages/cookies";
import SessionExpirationWarning from "./components/SessionExpirationWarning";
import ErrorBoundary from "./components/ErrorBoundary";
import AppBanner from "./components/AppBanner";
import ErrorLogList from "./pages/errorlog";
import About from "./pages/about";
import InvitationPage from "./pages/invitation";
import RejectInvitationPage from "./pages/invitation-reject";
import MisPacientes from "./pages/mis-pacientes";
import InvitationManager from "./pages/invitation-manager";

const App = () => {
  const [swaggerClient, setSwaggerClient] = useState(null);
  const [client] = useState(() => {
    const c = new ApiClient();
    delete c.defaultHeaders['User-Agent'];
    return c;
  });
  const [tokenReady, setTokenReady] = useState(false);

  const logoutOnceRef = useRef(false);
  const swaggerClientRef = useRef(null);
  const isRefreshingRef = useRef(false); // Flag para evitar múltiples refreshes
  const tokenInitializedRef = useRef(false); // Flag para saber si ya inicializamos el token

  const state = useSelector((state) => state);
  const dispatch = useDispatch();

  // Actualizar ref cuando cambie swaggerClient
  useEffect(() => {
    swaggerClientRef.current = swaggerClient;
  }, [swaggerClient]);

  const safeLogout = useCallback(() => {
    if (logoutOnceRef.current) return;
    logoutOnceRef.current = true;
    dispatch(logout(swaggerClientRef.current?.userApi));
  }, [dispatch]);

  const tokenError = useCallback((req) => {
    req.on("response", (res) => {
      if (res.status === 401 || res.status === 403) {
        safeLogout();
      }
    });
  }, [safeLogout]);

  const refreshBearerToken = useCallback((userApi) => {
    // Evitar múltiples refreshes simultáneos
    if (isRefreshingRef.current) {
      console.log("Ya hay un refresh en progreso, saltando...");
      return;
    }

    // Verificar que existe el refresh token
    if (!state?.auth?.user?.refreshToken) {
      console.error("No hay refresh token disponible");
      dispatch(logout(swaggerClientRef.current?.userApi));
      setTokenReady(true);
      return;
    }

    isRefreshingRef.current = true; // Marcar que estamos refrescando
    console.log("Iniciando refresh del token...");

    let refreshTokenRequestDto = { token: state.auth.user.refreshToken };
    userApi.refreshToken(refreshTokenRequestDto, createApiCallback(
      (data) => {
        console.log("Token refrescado exitosamente");
        // Actualizar el cliente con el nuevo token
        client.authentications["bearerAuth"] = {
          accessToken: data.token,
        };
        client.defaultHeaders["Authorization"] =
          "Bearer " + client.authentications["bearerAuth"].accessToken;

        // Guardar las nuevas fechas de expiración en localStorage
        persistTokenData(data);

        // Actualizar el estado de Redux con los nuevos tokens y fechas
        dispatch(updateAuthUser({
          token: data.token,
          refreshToken: data.refreshToken,
          tokenExpiresAt: data.tokenExpiresAt instanceof Date 
            ? data.tokenExpiresAt.toISOString() 
            : data.tokenExpiresAt,
          refreshTokenExpiresAt: data.refreshTokenExpiresAt instanceof Date 
            ? data.refreshTokenExpiresAt.toISOString() 
            : data.refreshTokenExpiresAt
        }));

        isRefreshingRef.current = false; // Liberar el flag
        setTokenReady(true);
      },
      (errorMessage) => {
        console.error("Error al refrescar token:", errorMessage);
        isRefreshingRef.current = false; // Liberar el flag
        dispatch(logout(swaggerClientRef.current ? swaggerClientRef.current.userApi : null));
        setTokenReady(true);
      }
    ));
  }, [state?.auth?.user?.refreshToken, client, dispatch]);

  useEffect(() => {
    client.plugins = [tokenError];
  }, [client, tokenError]);

  // Crear las instancias de API una sola vez
  useEffect(() => {
    const userApi = new UserApi(client);
    const pageApi = new PageApi(client);
    const categoryApi = new CategoryApi(client);
    const questionApi = new QuestionApi(client);
    const answerApi = new AnswerApi(client);
    const roleApi = new RoleApi(client);
    const gameSessionApi = new GameSessionApi(client);
    const errorLogApi = new ErrorLogApi(client);
    const invitationApi = new UserInvitationApi(client);
    setSwaggerClient({
      userApi,
      pageApi,
      categoryApi,
      questionApi,
      answerApi,
      roleApi,
      gameSessionApi,
      errorLogApi,
      invitationApi,
    });
  }, [client]);

  // Effect 1: Inicializar token solo la primera vez
  useEffect(() => {
    if (!swaggerClient?.userApi || tokenInitializedRef.current) {
      return; // Ya inicializado o APIs no están listas
    }

    const currentToken = state?.auth?.user?.token;

    if (currentToken) {
      const isExpired = isTokenExpiredLocally();
      
      if (isExpired) {
        const refreshExpired = isRefreshTokenExpiredLocally();
        
        if (refreshExpired) {
          console.log("Refresh token expirado, cerrando sesión");
          dispatch(logout(swaggerClient.userApi));
          setTokenReady(true);
          tokenInitializedRef.current = true;
        } else {
          console.log("Token expirado, iniciando refresh inicial...");
          refreshBearerToken(swaggerClient.userApi);
          tokenInitializedRef.current = true;
        }
      } else {
        console.log("Token válido, configurando headers inicialmente...");
        client.authentications["bearerAuth"] = {
          accessToken: currentToken,
        };
        client.defaultHeaders["Authorization"] =
          "Bearer " + client.authentications["bearerAuth"].accessToken;
        setTokenReady(true);
        tokenInitializedRef.current = true;
      }
    } else {
      setTokenReady(true);
      tokenInitializedRef.current = true;
    }
  }, [swaggerClient, client, dispatch, state?.auth?.user?.token, refreshBearerToken]);

  // Effect 2: Actualizar headers cuando el token cambie DESPUÉS de la inicialización
  useEffect(() => {
    // Solo ejecutar si ya pasó la inicialización y no estamos refrescando
    if (!tokenInitializedRef.current || isRefreshingRef.current) {
      return;
    }

    const currentToken = state?.auth?.user?.token;
    if (currentToken) {
      console.log("Actualizando headers con nuevo token...");
      client.authentications["bearerAuth"] = {
        accessToken: currentToken,
      };
      client.defaultHeaders["Authorization"] =
        "Bearer " + client.authentications["bearerAuth"].accessToken;
    }
  }, [state?.auth?.user?.token, client]);

  if (!swaggerClient) {
    return <div>Loading...</div>;
  }

  // Si hay un usuario logueado pero el token no está listo, mostrar spinner
  if (state?.auth?.isLoggedIn && !tokenReady) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Preparando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <SwaggerContext.Provider value={swaggerClient}>
      <AppBanner />
      {state?.auth?.isLoggedIn && <SessionExpirationWarning userApi={swaggerClient.userApi} />}
      <ErrorBoundary>
        <Routes>
        {tokenReady ? (
          <>
            {/* GAME AND COMMON ROUTES - Accesible para TODOS los logueados (evaluado primero) */}
            <Route element={<PublicRoutes />}>
              <Route path="/perfil" element={<Profile />} />
              <Route path="/modo" element={<GameSelector />} />
              <Route path="/jugar" element={<Game />} />
              <Route path="/estadisticas" element={<Statistics />} />
              <Route path="/inicio" element={<GameSelector />} />
            </Route>

            {/* ADMIN ROUTES - Administración */}
            <Route element={<PrivateRoute />}>
              <Route path="/paginas" element={<Page />} />
              <Route path="/cookies" element={<CookiesPolicy />} />
              <Route path="/paginas/nueva" element={<Page />} />
              <Route path="/paginas/editar/:id" element={<Page />} />
              <Route path="/categorias" element={<Category />} />
              <Route path="/categorias/nueva" element={<Category />} />
              <Route path="/categorias/editar/:id" element={<Category />} />
              <Route path="/usuarios" element={<User />} />
              <Route path="/usuarios/nuevo" element={<User />} />
              <Route path="/usuarios/editar/:id" element={<User />} />
              <Route path="/error-logs" element={<ErrorLogList />} />
            </Route>

            {/* THERAPIST ROUTES - Gestión de invitaciones y pacientes */}
            <Route element={<TherapistRoute />}>
              <Route path="/gestor-invitaciones" element={<InvitationManager />} />
              <Route path="/mis-pacientes" element={<MisPacientes />} />
              <Route path="/mis-pacientes/ver/:userId" element={<MisPacientes />} />
            </Route>

            {/* 404 */}
            <Route element={<PrivateRoute />}>
              <Route path="*" element={<div>Not found</div>} />
            </Route>
          </>
        ) : null}
        <Route path="/" element={<Dashboard />} />
        <Route path="/sobre-el-proyecto" element={<About />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/complete/:token" element={<Register />} />
        <Route path="/password/restore" element={<Restore />} />
        <Route path="/password/restore/:token" element={<Restore />} />
        <Route path="/invitation/accept/:token" element={<InvitationPage />} />
        <Route path="/invitation/reject/:token" element={<RejectInvitationPage />} />
      </Routes>
      </ErrorBoundary>
    </SwaggerContext.Provider>
  );
};

export default App;
